export const LANGUAGE_CHOICE_STORAGE_KEY = "mansoq.languageChoice";
const LEGACY_LANGUAGE_STORAGE_KEY = "i18nextLng";
const FALLBACK_LANGUAGE = "en";

type LanguageStorage = Pick<Storage, "getItem" | "setItem">;

function matchLocale(
  candidate: string | null | undefined,
  availableLocales: readonly string[],
) {
  if (!candidate) return undefined;
  const normalized = candidate.trim().toLowerCase();
  const base = normalized.split("-")[0];
  return (
    availableLocales.find((locale) => locale.toLowerCase() === normalized) ??
    availableLocales.find((locale) => locale.toLowerCase() === base)
  );
}

export function resolveInitialLanguage({
  explicitChoice,
  tenantDefault,
  navigatorLanguages,
  availableLocales,
}: {
  explicitChoice?: string | null;
  tenantDefault?: string;
  navigatorLanguages: readonly string[];
  availableLocales: readonly string[];
}): string {
  const explicit = matchLocale(explicitChoice, availableLocales);
  if (explicit) return explicit;
  if (tenantDefault) {
    const tenant = matchLocale(tenantDefault, availableLocales);
    if (tenant) return tenant;
    console.warn(
      `Tenant default language "${tenantDefault}" is not an allowed locale; using "${FALLBACK_LANGUAGE}".`,
    );
    return FALLBACK_LANGUAGE;
  }
  for (const language of navigatorLanguages) {
    const detected = matchLocale(language, availableLocales);
    if (detected) return detected;
  }
  return FALLBACK_LANGUAGE;
}

export function readExplicitLanguageChoice(storage?: LanguageStorage) {
  if (!storage) return undefined;
  try {
    const choice = storage.getItem(LANGUAGE_CHOICE_STORAGE_KEY);
    if (choice) return choice;
    const legacy = storage.getItem(LEGACY_LANGUAGE_STORAGE_KEY);
    if (!legacy) return undefined;
    storage.setItem(LANGUAGE_CHOICE_STORAGE_KEY, legacy);
    return legacy;
  } catch {
    return undefined;
  }
}

export function rememberLanguageChoice(
  storage: LanguageStorage | undefined,
  language: string,
) {
  try {
    storage?.setItem(LANGUAGE_CHOICE_STORAGE_KEY, language);
  } catch {
    return;
  }
}
